import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { fmtTL, fmtDate } from "@/lib/format";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/StatusBadge";
import { Plus, Search, Edit, Trash2, ShoppingCart } from "lucide-react";
import { toast } from "sonner";
import ExportButton from "@/components/ExportButton";

const STATUS_LABELS = {
  draft: { label: "Taslak", variant: "default" },
  sent: { label: "Gönderildi", variant: "info" },
  received: { label: "Teslim Alındı", variant: "success" },
  cancelled: { label: "İptal", variant: "danger" },
};

export default function PurchaseOrders() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");

  const load = async () => {
    const params = {};
    if (q.trim()) params.q = q.trim();
    if (status !== "all") params.status = status;
    const { data } = await api.get("/purchase-orders", { params });
    setItems(data);
  };
  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [q, status]);

  const remove = async (id) => {
    if (!window.confirm("Bu satın alma siparişini silmek istediğinize emin misiniz?")) return;
    try {
      await api.delete(`/purchase-orders/${id}`);
      toast.success("Silindi");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Silinemedi");
    }
  };

  return (
    <div className="space-y-4">
      <div className="azd-card p-4 flex flex-wrap items-end gap-3 justify-between">
        <div className="flex flex-wrap gap-3 flex-1">
          <div className="relative min-w-[260px] flex-1">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B7280]" />
            <Input value={q} onChange={(e) => setQ(e.target.value)}
                   placeholder="Takip no, tedarikçi veya ürün ara..."
                   className="pl-9" data-testid="po-search" />
          </div>
          <div className="min-w-[180px]">
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger data-testid="po-status-filter"><SelectValue placeholder="Durum" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tüm Durumlar</SelectItem>
                {Object.entries(STATUS_LABELS).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="flex gap-2">
          <ExportButton
            data={items}
            filename="satin-alma-siparisleri"
            columns={[
              { key: "tracking_no", label: "Takip No" },
              { key: "order_date", label: "Sipariş Tarihi" },
              { key: "supplier_name", label: "Tedarikçi" },
              { key: "grand_total", label: "Toplam" },
              { key: "delivery_date", label: "Teslim Tarihi" },
              { key: "payment_method", label: "Ödeme Şekli" },
              { key: "status", label: "Durum" },
            ]}
          />
          <Button onClick={() => navigate("/purchase-orders/new")}
                  data-testid="po-new"
                  className="bg-[#0B3676] hover:bg-[#08285A] text-white">
            <Plus size={16} className="mr-1" /> Yeni Satın Alma Siparişi
          </Button>
        </div>
      </div>

      <div className="azd-card overflow-hidden">
        <div className="overflow-x-auto azd-scrollbar">
          <table className="w-full text-sm">
            <thead className="bg-[#F6F8FB] text-[11px] uppercase tracking-wider text-[#6B7280]">
              <tr>
                <th className="text-left px-4 py-3 font-semibold">Takip No</th>
                <th className="text-left px-4 py-3 font-semibold">Sipariş Tarihi</th>
                <th className="text-left px-4 py-3 font-semibold">Tedarikçi</th>
                <th className="text-right px-4 py-3 font-semibold">Ürün Sayısı</th>
                <th className="text-right px-4 py-3 font-semibold">Toplam</th>
                <th className="text-left px-4 py-3 font-semibold">Teslim Tarihi</th>
                <th className="text-center px-4 py-3 font-semibold">Durum</th>
                <th className="text-right px-4 py-3 font-semibold">İşlem</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 ? (
                <tr><td colSpan={8} className="p-8 text-center text-[#6B7280]">
                  <ShoppingCart size={28} className="mx-auto mb-2 opacity-40" />
                  Henüz satın alma siparişi kaydı yok
                </td></tr>
              ) : items.map((p) => (
                <tr key={p.id} data-testid={`po-row-${p.id}`}
                    className="border-t border-[#E5E7EB] hover:bg-[#F6F8FB]/50">
                  <td className="px-4 py-3 font-mono text-xs font-bold text-[#08285A]">{p.tracking_no}</td>
                  <td className="px-4 py-3 text-xs">{fmtDate(p.order_date)}</td>
                  <td className="px-4 py-3 font-semibold">{p.supplier_name}</td>
                  <td className="px-4 py-3 text-right">{(p.items || []).length}</td>
                  <td className="px-4 py-3 text-right font-bold text-[#0B3676]">{fmtTL(p.grand_total)}</td>
                  <td className="px-4 py-3 text-xs">{p.delivery_date ? fmtDate(p.delivery_date) : "-"}</td>
                  <td className="px-4 py-3 text-center">
                    <Badge variant={STATUS_LABELS[p.status]?.variant || "default"}>
                      {STATUS_LABELS[p.status]?.label || p.status}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <button onClick={() => navigate(`/purchase-orders/${p.id}/edit`)}
                            data-testid={`po-edit-${p.id}`}
                            className="p-1.5 text-[#6B7280] hover:text-[#0B3676] rounded"
                            title="Düzenle">
                      <Edit size={14} />
                    </button>
                    <button onClick={() => remove(p.id)}
                            data-testid={`po-del-${p.id}`}
                            className="p-1.5 text-[#6B7280] hover:text-[#DC2626] hover:bg-[#DC2626]/10 rounded ml-1"
                            title="Sil">
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
